import { useCallback } from 'react';
import { endOfDay, isBefore } from 'date-fns';

import {
  actions,
  isOwner,
  getTaskStatusConfig,
  projectStatusByCode,
  taskStatusesConfig,
} from 'entities';

import { Table, Row } from 'components/Blocks';
import { EntityStatusTag } from 'components/Entities';

import { StatusSelect } from 'pages/Projects/components';

import { SubTaskName } from './Name';
import { SubTaskDate } from './Date';
import { SubTaskOwner } from './Owner';

import { useTaskModal } from '../../context.taskModal';
import { useUser } from 'hooks';

export function SubTaskList() {
  const loggedUser = useUser();

  const { task, updateTask, project } = useTaskModal();

  const shouldDisableDate = (date: Date | null): boolean => {
    if (!date) return false;

    const taskEnd = endOfDay(new Date(task.expectedDate as string));

    return isBefore(endOfDay(taskEnd), date);
  };

  const changeStatus = actions.projects.changeSubTaskStatus.use({
    onSuccess: (_, { subTask, status }) => {
      updateTask((old) => ({
        ...old,
        subTasks: old.subTasks.map((sub) => (sub.id === subTask ? { ...sub, status } : sub)),
      }));
    },
  });

  const handleSubTaskStatusChange = useCallback(
    async (status: string, id: string) => {
      await changeStatus.execute({
        project: task.projectId,
        task: task.id,
        subTask: id,
        status,
      });
    },
    [task, changeStatus],
  );

  const statusConfig = getTaskStatusConfig(task.status);
  const projectConfig = projectStatusByCode[project.status];

  const isBlocked = [
    statusConfig.blocks.fullBlock,
    statusConfig.blocks.addSubtasks,
    projectConfig.blocks.fullBlock,
  ].some(Boolean);

  if (!task.subTasks.length) return null;

  return (
    <Table
      items={task.subTasks}
      uniqueKey="id"
      itemsPerPage={25}
      columns={[
        {
          render: (subTask) => <SubTaskName subTask={subTask} />,
        },
        {
          render: (subTask) => <SubTaskOwner subTask={subTask} />,
        },
        {
          render: (subTask) => (
            <SubTaskDate subTask={subTask} shouldDisableDate={shouldDisableDate} />
          ),
        },
        {
          render: (subTask) => (
            <Row justifyContent="center">
              {isOwner(subTask, loggedUser) &&
              !isBlocked &&
              !getTaskStatusConfig(subTask.status).blocks.changeSubStatus ? (
                <StatusSelect
                  value={subTask.status}
                  onChange={(status) => handleSubTaskStatusChange(status, subTask.id)}
                  statusConfig={taskStatusesConfig}
                  loading={changeStatus.loading}
                  className="min-h-0 py-0.5 text-sm [&_svg]:size-3"
                />
              ) : (
                <EntityStatusTag target="subTask" status={subTask.status} />
              )}
            </Row>
          ),
        },
      ]}
    />
  );
}
